import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env/environment';

export interface FileUpload {
  id: number;
  user_id: number;
  uploadDate: string;
  filename: string;
  original_filename: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  table_name: string;
  record_id: string;
  standort_name?: string;
  description?: string;
  tags?: string[];
  is_public?: boolean;
  download_count?: number;
  last_downloaded?: string;
  checksum?: string;
  user_name: string;
  user_full_name?: string;
  mobile_id?: string;
  app_key?: string;
  created_at: string;
  updated_at: string;
}

export interface FileUploadResponse {
  data: FileUpload[] | { [key: string]: FileUpload[] };
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

@Injectable({
  providedIn: 'root'
})
export class DokumenteService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  getDokumente(tableName: string, recordId: string, page: number = 1, limit: number = 25, search?: string): Observable<FileUploadResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('table_name', tableName)
      .set('record_id', recordId);

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<FileUploadResponse>(`${this.apiUrl}/fileupload`, { params });
  }

  // Get all documents for all standorte of a given client
  getClientDokumente(clientId: string, page: number = 1, limit: number = 25, search?: string): Observable<FileUploadResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<FileUploadResponse>(`${this.apiUrl}/fileupload/client/${clientId}`, { params });
  }

  // Get all documents for a specific standort
  getStandortDokumente(standortId: string, page: number = 1, limit: number = 25, search?: string): Observable<FileUploadResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<FileUploadResponse>(`${this.apiUrl}/fileupload/standort/${standortId}`, { params });
  }

  getDokument(id: number): Observable<FileUpload> {
    return this.http.get<FileUpload>(`${this.apiUrl}/fileupload/${id}`);
  }

  createDokument(dokument: FormData): Observable<FileUpload> {
    return this.http.post<FileUpload>(`${this.apiUrl}/fileupload`, dokument);
  }

  updateDokument(id: number, dokument: Partial<FileUpload>): Observable<FileUpload> {
    return this.http.put<FileUpload>(`${this.apiUrl}/fileupload/${id}`, dokument);
  }

  deleteDokument(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/fileupload/${id}`);
  }

  downloadDokument(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/fileupload/${id}/download`, { responseType: 'blob' });
  }

  previewDokument(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/fileupload/${id}/preview`, { responseType: 'blob' });
  }

  // Request a pre-signed URL for a file (no JWT needed for final file GET)
  signDokumentUrl(id: number, ttlSeconds: number = 300, download: boolean = false): Observable<{ result: number; url: string; expiresAt: string }> {
    const params = new HttpParams()
      .set('ttl', ttlSeconds.toString())
      .set('dl', download ? '1' : '0');
    return this.http.get<{ result: number; url: string; expiresAt: string }>(`${this.apiUrl}/fileupload/${id}/sign`, { params });
  }

  uploadDokument(file: File, tableName: string, recordId: string, description?: string): Observable<FileUpload> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('table_name', tableName);
    formData.append('record_id', recordId);
    if (description) {
      formData.append('description', description);
    }

    return this.http.post<FileUpload>(`${this.apiUrl}/fileupload`, formData);
  }

  /**
   * Upload a binary file via the FilePond processor, which actually stores the
   * file on disk and creates the fileupload record. Returns the numeric upload id.
   * (The generic POST /fileupload only creates a metadata record and does not
   * persist binary content, so real uploads must go through this endpoint.)
   */
  uploadViaFilePond(file: File, tableName: string, recordId: string): Observable<number> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('table_name', tableName);
    formData.append('record_id', recordId);
    return this.http
      .post(`${this.apiUrl}/filepond/process`, formData, { responseType: 'text' })
      .pipe(
        map((res) => {
          // Server returns the numeric DB id (possibly with a "FILEID:" debug prefix).
          const match = (res || '').toString().match(/(\d+)\s*$/);
          return match ? parseInt(match[1], 10) : NaN;
        })
      );
  }
}
